import React from "react";

import { IconInfoItalicOutlined } from "~/components/icon";
import { RefreshIntervalSelector } from "~/components/refresh-interval-selector";
import type { RefreshInterval } from "~/components/refresh-interval-selector/utils";
import { Truncated } from "~/components/truncated";
import { useScope } from "~/tools/impulse";
import { prop } from "~/tools/prop";

import { HealthTimelineChart } from "../../../../components/_health-timeline-chart";
import { Widget, WidgetBody, WidgetHeader } from "../../../../components/_widget";
import { TimelineRangeSelectorExtended } from "../../../../components/react/timeline/TimelineRangeSelectorExtended";
import { queryMonitorCheckStatusHealthHistory } from "../../../../queries";
import { useQuery } from "../../../../queries/react-query";
import { useRefetchQueriesInNamespace } from "../../../../queries/use-refetch-queries-in-namespace";

import { useMonitorStatusDrawerContext } from "./MonitorStatusDrawer/use-monitor-status-drawer-context";
import { MONITOR_STATUS_DRAWER_QUERY_NAMESPACE } from "./_query-namespace";

export interface MonitorHealthTimelineProps {
	identifier: string;
	disabled?: boolean;
}

export const MonitorHealthTimeline: React.FC<MonitorHealthTimelineProps> = ({ identifier, disabled = false }) => {
	const scope = useScope();
	const { timestamp, timeRange } = useMonitorStatusDrawerContext();
	const timeRangeValue = timeRange.getValue(scope);
	const isTimestampInRange = timeRangeValue.contains(timestamp.getValue(scope));
	const [refreshInterval, setRefreshInterval] = React.useState<RefreshInterval>("off");
	const [isRefetching, refetchQueries] = useRefetchQueriesInNamespace(MONITOR_STATUS_DRAWER_QUERY_NAMESPACE);

	const { data: health, isLoading } = useQuery({
		namespace: MONITOR_STATUS_DRAWER_QUERY_NAMESPACE,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,

		select: prop("healthStateChanges"),

		...queryMonitorCheckStatusHealthHistory({
			identifier,
			timeRange: timeRangeValue,
		}),
	});

	return (
		<Widget className="pb-3">
			<WidgetHeader className="flex flex-wrap justify-between gap-y-4" size="xl" title="Monitor health timeline">
				<div className="flex flex-row flex-wrap items-center justify-end gap-4">
					{!isTimestampInRange && (
						<Truncated className="ml-auto flex select-none items-center gap-1 font-light text-gray-600 italic" el="div">
							<IconInfoItalicOutlined className="inline-block text-(--foundation-blue-600)" />
							<span className="text-13">The monitor status time is outside the selected interval</span>
						</Truncated>
					)}

					<TimelineRangeSelectorExtended
						contextName="monitor status"
						disabled={isLoading || disabled}
						timeRange={timeRange}
					/>

					<RefreshIntervalSelector
						disabled={timeRangeValue.isSpecific()}
						loading={isRefetching}
						onChange={setRefreshInterval}
						onRefresh={refetchQueries}
						value={refreshInterval}
					/>
				</div>
			</WidgetHeader>

			<WidgetBody card className="px-10 py-3">
				<HealthTimelineChart disabled={disabled} health={health} timeRange={timeRange} timestamp={timestamp} />
			</WidgetBody>
		</Widget>
	);
};
